import {
  Component,
  ComponentRef,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  InputSignal,
  resource,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { delay, firstValueFrom, Observable, of } from 'rxjs';
import { mapping } from '../../tile-blueprint/mapping';
import {
  AtomicBlueprint,
  BoTable,
  BoTableRow,
  FilterValue,
  GenericTable,
  TileBlueprint,
  TileService,
} from 'src/openapi-client';
import { FocusService } from 'src/app/common/services/focus.service';

export interface TileBlueprintSupported {
  elements: InputSignal<AtomicBlueprint[]>;
}

type TileComponentType = InstanceType<(typeof mapping)[keyof typeof mapping]>;

@Component({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-tile',
  imports: [],
  templateUrl: './tile.component.html',
  styleUrl: './tile.component.scss',
})
export class TileComponent {
  readonly tileService = inject(TileService);

  readonly id = input.required<string>();

  readonly filter = input.required<FilterValue[]>();

  private readonly focus = inject(FocusService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly isFullscreen = computed(() => this.focus.focusedTileId() === this.id());

  readonly col = input(2);
  readonly row = input(1);

  @HostBinding('class')
  hostClass = '';

  readonly metadata = resource({
    params: () => ({ id: this.id() }),
    loader: async ({ params }) => await firstValueFrom(this.tileService.loadTile(params.id)),
  });

  readonly content = resource({
    params: () => ({ id: this.id(), filter: this.filter() }),
    loader: async ({ params }) => await firstValueFrom(this.tileService.loadTileElements(params.id, params.filter)),
  });

  readonly displayedTable = computed<BoTableRow[] | undefined>(() => {
    if (this.content.status() !== 'resolved') {
      return undefined;
    }
    const table = this.content.value()?.find(element => element.type === 'genericTable') as GenericTable | undefined;
    return (table?.content as BoTable | undefined)?.rows;
  });

  @ViewChild('container', { read: ViewContainerRef, static: true })
  private readonly container!: ViewContainerRef;

  @HostListener('window:resize')
  updateFullscreenHeight(): void {
    const el = this.host.nativeElement;
    const top = el.offsetParent?.getBoundingClientRect().top ?? 0;
    const bottom = el.closest('wct-home')?.getBoundingClientRect().bottom ?? window.innerHeight;
    el.style.setProperty('--fullscreen-height', `${bottom - top}px`);
  }

  @HostListener('document:keydown.escape')
  exitFullscreen(): void {
    if (this.isFullscreen()) {
      this.focus.close();
    }
  }

  constructor() {
    let wasFullscreen = false;
    effect(() => {
      const fullscreen = this.isFullscreen();
      if (fullscreen !== wasFullscreen) {
        wasFullscreen = fullscreen;
        this.updateFullscreenHeight();
        setTimeout(() => window.dispatchEvent(new Event('resize')));
      }
    });

    effect(() => {
      this.hostClass = `col-${this.col()} row-${this.row()}${this.isFullscreen() ? ' is-fullscreen' : ''}`;
    });

    effect(() => {
      if (this.metadata.status() == 'resolved' && this.content.status() == 'resolved') {
        this.spawn(this.metadata.value()!.type, this.content.value()!);
      } else {
        this.container.clear();
      }
    });
  }

  private spawn(type: TileBlueprint['type'], elements: AtomicBlueprint[]) {
    this.container.clear();
    const component: ComponentRef<TileComponentType> = this.container.createComponent(mapping[type]);
    component.setInput('elements', elements);
    component.setInput('tileId', this.id());
    return component;
  }
}

